import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [kundensicht, setKundensicht] = useState(null);
  const serverView = useRef(null);
  const [activeTenantId, setActiveTenantId] = useState(() => {
    const mode = localStorage.getItem("sc_tenant_mode");
    return mode && mode !== "all" ? mode : null;
  });
  const [loading, setLoading] = useState(true);

  const applyMe = useCallback((data) => {
    setUser(data.user);
    setTenants(data.tenants || []);
    setIsSuperAdmin(!!data.is_super_admin);
    const ks = data.kundensicht || null;
    serverView.current = ks;
    setKundensicht(ks);
    setActiveTenantId((prev) => {
      const ids = (data.tenants || []).map((t) => t.id);
      // In sicherer Kundensicht ist der Mandant serverseitig gepinnt.
      if (ks?.active && ks.tenant_id) return ks.tenant_id;
      const mode = localStorage.getItem("sc_tenant_mode");
      if (mode === "all") return null;              // explizit „Alle Mandanten" (global)
      if (mode && ids.includes(mode)) return mode;  // gemerkter Mandant
      if (prev && ids.includes(prev)) return prev;
      return ids[0] || null;                        // Erstlogin-Default
    });
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyMe(data);
      return data;
    } catch {
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyMe]);

  const chooseTenant = useCallback((id) => {
    // View-As is server-pinned; keep the staff selection for the return journey.
    if (serverView.current?.active) return;
    localStorage.setItem("sc_tenant_mode", id ? id : "all");
    setActiveTenantId(id || null);
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth, AuthCallback establishes the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.mfa_required) return { mfa_required: true, mfa_token: data.mfa_token };
    if (data?.mfa_enrollment_required) return { mfa_enrollment_required: true, setup_token: data.setup_token, email: data.email };
    return await checkAuth();
  };
  const loginVerify2fa = async (mfaToken, code) => {
    await api.post("/auth/login/2fa", { mfa_token: mfaToken, code });
    return await checkAuth();
  };
  const enroll2faStart = async (setupToken) => (await api.post("/auth/2fa/enroll/start", { setup_token: setupToken })).data;
  const enroll2faConfirm = async (setupToken, code) => {
    const { data } = await api.post("/auth/2fa/enroll/confirm", { setup_token: setupToken, code });
    await checkAuth();
    return data;
  };
  const register = async (email, password, name) => {
    await api.post("/auth/register", { email, password, name });
    await checkAuth();
  };
  const logout = async () => {
    // Fail-safe: Client MUSS abgemeldet werden, auch wenn der Netzwerk-Call scheitert
    // (sonst bliebe der Nutzer sichtbar eingeloggt, obwohl die Sitzung serverseitig endet).
    try { await api.post("/auth/logout"); } catch { /* trotzdem lokal abmelden */ }
    setUser(false);
    setTenants([]);
    setIsSuperAdmin(false);
    serverView.current = null;
    setKundensicht(null);
  };

  // Sichere Kundensicht (View-As): Staff sieht das Cockpit EINES Mandanten, nur-lesend.
  // Die echte Staff-Identität bleibt erhalten (kein Identitäts-Tausch).
  const startKundensicht = async (tenantId) => {
    await api.post("/staff/kundensicht/start", { tenant_id: tenantId });
    if (!kundensicht?.active) localStorage.setItem("sc_tenant_mode", activeTenantId || "all");
    return await checkAuth();
  };
  const stopKundensicht = async () => {
    await api.post("/staff/kundensicht/stop");
    // applyMe validates the remembered selection against the restored staff scope.
    // Keep View-As active until /auth/me confirms the actual server session.
    return await checkAuth();
  };

  // Jury-Demo (passwortlos, nur über gültigen Einladungslink): startet eine isolierte Sitzung.
  const startJuryDemo = async (invite) => {
    const { data } = await api.post("/jury-demo/start", { invite: invite || null });
    localStorage.setItem("sc_jury", "1");
    localStorage.removeItem("sc_tenant_mode");
    await checkAuth();
    return data;
  };
  const endJuryDemo = async () => {
    try { await api.post("/jury-demo/end"); } catch { /* Sitzung evtl. bereits beendet */ }
    localStorage.removeItem("sc_jury");
  };

  const activeTenant = tenants.find((t) => t.id === activeTenantId) || null;

  return (
    <AuthContext.Provider
      value={{
        user, tenants, isSuperAdmin, loading, activeTenant, activeTenantId, kundensicht,
        setActiveTenantId: chooseTenant, login, loginVerify2fa, register, logout, checkAuth, refreshTenants: checkAuth,
        enroll2faStart, enroll2faConfirm,
        startKundensicht, stopKundensicht,
        startJuryDemo, endJuryDemo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
